// Carry & Basis card — one token's implied-carry term structure. Mirrors
// Market Probabilities' MarketLadderCard chrome (banner + tile row + gridded
// table + footer) so the two boards read as siblings.
//
// Columns, left-to-right: Tenor | divider | [Futures Market: Futures Mark,
// Fwd Points, Forward Factor, Futures Carry] | divider | [Options Market:
// Future, Fwd Points, Forward Factor, USD Discount, Base Discount, Forward
// Carry, Basis]. Tenor sits outside both groups (it's the shared row key,
// not data from either source). The two groups are genuinely different
// data sources — Futures Market is Deribit's real futures_snapshot
// mark/index price (attach-futures-snapshot.ts); Options Market is entirely
// options-implied off ONE vol-surface call (build-carry-curve.ts, from
// latest_vol_surface) — hence the group headers and vertical dividers, not
// just a stylistic split.
//
// Forward Factor is the raw F/S growth multiplier (so Forward Carry =
// ln(Forward Factor)/yearFrac) — shown alongside Fwd Points (the absolute
// F-S difference) as the ratio form of the same relationship. It needs no
// yearFrac, so it's real for the Perpetual row too, unlike Futures Carry.
//
// No per-tenor Futures Index column — Deribit's index price doesn't vary
// by expiry, so it's shown once, card-level, in the footer's "Index" tile
// instead of repeating an identical number down every row. Same reasoning
// already applied to per-row timestamps: shown once as the "Futures As Of"
// footer tile (alongside "Options As Of" for the vol surface's own poll).
// Funding rate (Deribit's funding_8h, annualized) is the same story — it's
// a PERPETUAL-only concept with no per-tenor equivalent, so it's a footer
// tile ("Funding (Ann.)") rather than a curve column.
//
// The perpetual has no expiry, so it renders as a standalone anchor row
// (PerpRow) above the tenor-keyed curve rows, Options Market side blank —
// there's no vol-surface "now" tenor to compare it against. It also has no
// Futures Carry (nothing to annualize against without an expiry) — its Fwd
// Points is still shown, since that's a plain price difference.
//
// Per the design discussion (see project_exotics_pricer_ux_iteration),
// USD/Base Discount are DISTINCT curves from Forward Carry, not one "yield
// curve" — see types.ts for the full taxonomy and the Basis tautology
// caveat (Base Discount here is derived off the same Future used in Forward
// Carry, so Basis reads as a sanity check, not an independent cross-market
// signal). Each group has its own "Fwd Points" (Futures: mark - index;
// Options: future - spot) and its own annualized carry rate (Futures:
// ln(mark/index)/yearFrac; Options: ln(future/spot)/yearFrac, i.e. Forward
// Carry) — same concepts, two independent sources, using the SAME yearFrac
// per tenor so the two rates are genuinely comparable side by side.
import { FC } from 'react';
import { Box, Stack, Typography } from '@mui/material';

import Number from '../../components/v2/number';
import CryptoIcon from '../../components/v2/crypto-icon';
import {
  dilettaPanelSx,
  DILETTA_BANNER_BG,
  DILETTA_TITLE,
  DILETTA_DIVIDER,
} from '../../components/v2/widgets/exotics-shared';
import { CarryCurveMarket, PerpPoint } from './types';
import {
  ACCENT_SOFT,
  BORDER_SUBTLE,
  NEGATIVE,
  POSITIVE,
  TEXT_MUTED,
  TEXT_PRIMARY,
  TILE_BG,
} from './chrome';

// One shared column-track definition so the header row and every curve row
// land on identical vertical edges. Track layout: Tenor (1) | divider
// (16px) | Futures Market: Futures Mark, Fwd Points, Forward Factor,
// Futures Carry (4) | divider (16px) | Options Market: Future, Fwd Points,
// Forward Factor, USD Discount, Base Discount, Forward Carry, Basis (7).
// The 16px tracks are real grid columns, not a border-on-a-cell hack —
// every row (group header, column header, data rows, the perp anchor row)
// renders a DividerCell there, so the vertical rules are pixel-identical
// and contiguous top-to-bottom rather than a set of separate per-row
// borders that can look fragmented.
const CURVE_GRID = 'minmax(0, 1fr) 16px repeat(4, minmax(0, 1fr)) 16px repeat(7, minmax(0, 1fr))';

const ROW_GRID_SX = {
  display: 'grid',
  gridTemplateColumns: CURVE_GRID,
  alignItems: 'center',
  columnGap: 0,
  px: 1.75,
} as const;

const HEAD_SX = {
  fontSize: 8,
  color: TEXT_MUTED,
  textTransform: 'uppercase' as const,
  letterSpacing: '.05em',
  fontWeight: 600,
};

// One word per line, right-aligned — used for every multi-column-group
// header (both Futures Market and Options Market), which read as cramped
// single-line labels once several sit side by side in a narrow column.
// Right-aligned (not centred) so each word's edge lines up with the
// right-aligned numeric cells underneath.
const StackedHeadCell: FC<{ label: string }> = ({ label }) => (
  <Box>
    {label.split(' ').map((word) => (
      <Typography key={word} sx={{ ...HEAD_SX, textAlign: 'right' }}>
        {word}
      </Typography>
    ))}
  </Box>
);

const GROUP_HEAD_SX = {
  fontSize: 8.5,
  color: ACCENT_SOFT,
  textTransform: 'uppercase' as const,
  letterSpacing: '.08em',
  fontWeight: 700,
  textAlign: 'center' as const,
};

// The vertical rule between Futures Market and Options Market — see
// CURVE_GRID's header comment. `alignSelf: stretch` makes this grid item
// fill its row's full height regardless of that row's content (a single
// group-title line, a column-label line, or a two-line data cell), so the
// centred 1.5px bar always reaches top-to-bottom of whichever row it's in.
const DividerCell: FC = () => (
  <Box sx={{ alignSelf: 'stretch', display: 'flex', justifyContent: 'center' }}>
    <Box sx={{ width: '1.5px', backgroundColor: DILETTA_DIVIDER }} />
  </Box>
);

// Sign-coloured annualized-rate cell — green for positive carry (contango),
// red for negative (backwardation), matching the platform-wide convention.
// A null value (not yet live) renders a muted em-dash rather than 0.00%.
const RateCell: FC<{ value: number | null; muted?: boolean }> = ({ value, muted }) => (
  <Box sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
    {value === null ? (
      <Box component="span" sx={{ color: TEXT_MUTED, fontSize: 11 }}>
        {'—'}
      </Box>
    ) : (
      <Box
        component="span"
        sx={{ color: muted ? TEXT_MUTED : value >= 0 ? POSITIVE : NEGATIVE, fontSize: 11 }}
      >
        <Number value={value} format="signed-percent" role="caption" />
      </Box>
    )}
  </Box>
);

// Discount-factor cell — the raw DF to 4dp as the headline figure (e.g.
// "0.9634"), with its annualized rate as a muted second line (mirrors the
// Tenor/Countdown two-line pattern). DF itself isn't sign-coloured (it's a
// discount factor, not a carry/backwardation signal); the rate subline
// keeps the platform-wide sign convention.
const DfCell: FC<{ df: number | null; rate: number | null }> = ({ df, rate }) => (
  <Box sx={{ textAlign: 'right' }}>
    <Typography
      sx={{
        fontFamily: 'monospace',
        fontSize: 11,
        color: df === null ? TEXT_MUTED : TEXT_PRIMARY,
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {df === null ? '—' : df.toFixed(4)}
    </Typography>
    <Typography
      sx={{
        fontFamily: 'monospace',
        fontSize: 9,
        color: rate === null ? TEXT_MUTED : rate >= 0 ? POSITIVE : NEGATIVE,
      }}
    >
      {rate === null ? '' : `${rate >= 0 ? '+' : ''}${(rate * 100).toFixed(2)}%`}
    </Typography>
  </Box>
);

// Plain price cell — the reference figure (Future), no sign colour.
const PriceCell: FC<{ value: number | null; decimals: number }> = ({ value, decimals }) => (
  <Typography
    sx={{
      textAlign: 'right',
      fontFamily: 'monospace',
      fontSize: 11,
      color: value === null ? TEXT_MUTED : TEXT_PRIMARY,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {value === null
      ? '—'
      : value.toLocaleString('en-GB', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}
  </Typography>
);

// Signed price cell — Forward Points (F - S). Sign-coloured like RateCell,
// same convention (positive = contango/green, negative = backwardation/red).
const SignedPriceCell: FC<{ value: number | null; decimals: number }> = ({ value, decimals }) => (
  <Typography
    sx={{
      textAlign: 'right',
      fontFamily: 'monospace',
      fontSize: 11,
      color: value === null ? TEXT_MUTED : value >= 0 ? POSITIVE : NEGATIVE,
      fontVariantNumeric: 'tabular-nums',
    }}
  >
    {value === null
      ? '—'
      : `${value >= 0 ? '+' : ''}${value.toLocaleString('en-GB', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}`}
  </Typography>
);

// Forward Factor cell — F/S as a raw ratio, e.g. "1.0023". Fixed 4dp
// regardless of the token's own price-decimals setting (it's a ratio near
// 1.0, not a price level) — same fixed precision as DfCell's raw DF figure.
// Sign-coloured relative to 1.0 (>1 = contango/green, <1 = backwardation/
// red), matching the platform-wide convention used for Fwd Points/Carry.
const FactorCell: FC<{ value: number | null }> = ({ value }) => (
  <Typography
    sx={{
      textAlign: 'right',
      fontFamily: 'monospace',
      fontSize: 11,
      fontVariantNumeric: 'tabular-nums',
      color: value === null ? TEXT_MUTED : value >= 1 ? POSITIVE : NEGATIVE,
    }}
  >
    {value === null ? '—' : value.toFixed(4)}
  </Typography>
);

// Live d/h/m countdown to expiry, e.g. "23d 4h 12m" / "4h 12m" / "12m" /
// "expired" once the clock runs out. Recomputed every render off the
// ticking `now` (see use-now.ts), not frozen at the last vol-surface poll.
const fmtCountdown = (expiryMs: number, now: Date): string => {
  const ms = expiryMs - now.getTime();
  if (ms <= 0) return 'expired';
  const totalMins = Math.floor(ms / 60000);
  const days = Math.floor(totalMins / (24 * 60));
  const hours = Math.floor((totalMins % (24 * 60)) / 60);
  const mins = totalMins % 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  if (hours > 0) return `${hours}h ${mins}m`;
  return `${mins}m`;
};

// The perpetual has no expiry, so it can't sit in the tenor-keyed curve
// array — rendered as a standalone anchor row above it (see types.ts's
// PerpPoint). Options Market side is all em-dash: there's no vol-surface
// "now" tenor to compare against.
const PerpRow: FC<{ perp: PerpPoint; decimals: number }> = ({ perp, decimals }) => (
  <Box sx={{ ...ROW_GRID_SX, py: 0.75, borderTop: `1px solid ${BORDER_SUBTLE}` }}>
    <Box>
      <Typography
        sx={{ fontFamily: 'monospace', fontSize: 11, color: TEXT_PRIMARY, fontWeight: 600 }}
      >
        Perpetual
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 9, color: TEXT_MUTED }}>
        No expiry
      </Typography>
    </Box>
    <DividerCell />
    <PriceCell value={perp.futuresMark} decimals={decimals} />
    <SignedPriceCell value={perp.futuresFwdPoints} decimals={decimals} />
    <FactorCell value={perp.futuresForwardFactor} />
    <RateCell value={null} />
    <DividerCell />
    <PriceCell value={null} decimals={decimals} />
    <SignedPriceCell value={null} decimals={decimals} />
    <FactorCell value={null} />
    <DfCell df={null} rate={null} />
    <DfCell df={null} rate={null} />
    <RateCell value={null} />
    <RateCell value={null} />
  </Box>
);

const CurveRow: FC<{
  tenor: string;
  expiryMs: number;
  future: number | null;
  forwardPoints: number | null;
  forwardFactor: number | null;
  usdDf: number | null;
  usdDiscount: number | null;
  baseDf: number | null;
  baseDiscount: number | null;
  forwardCarry: number | null;
  basis: number | null;
  futuresMark: number | null;
  futuresFwdPoints: number | null;
  futuresForwardFactor: number | null;
  futuresCarry: number | null;
  decimals: number;
  now: Date;
}> = ({
  tenor,
  expiryMs,
  future,
  forwardPoints,
  forwardFactor,
  usdDf,
  usdDiscount,
  baseDf,
  baseDiscount,
  forwardCarry,
  basis,
  futuresMark,
  futuresFwdPoints,
  futuresForwardFactor,
  futuresCarry,
  decimals,
  now,
}) => (
  <Box sx={{ ...ROW_GRID_SX, py: 0.75, borderTop: `1px solid ${BORDER_SUBTLE}` }}>
    <Box>
      <Typography
        sx={{ fontFamily: 'monospace', fontSize: 11, color: TEXT_PRIMARY, fontWeight: 600 }}
      >
        {tenor}
      </Typography>
      <Typography sx={{ fontFamily: 'monospace', fontSize: 9, color: TEXT_MUTED }}>
        {fmtCountdown(expiryMs, now)}
      </Typography>
    </Box>
    <DividerCell />
    <PriceCell value={futuresMark} decimals={decimals} />
    <SignedPriceCell value={futuresFwdPoints} decimals={decimals} />
    <FactorCell value={futuresForwardFactor} />
    <RateCell value={futuresCarry} />
    <DividerCell />
    <PriceCell value={future} decimals={decimals} />
    <SignedPriceCell value={forwardPoints} decimals={decimals} />
    <FactorCell value={forwardFactor} />
    <DfCell df={usdDf} rate={usdDiscount} />
    <DfCell df={baseDf} rate={baseDiscount} />
    <RateCell value={forwardCarry} />
    <RateCell value={basis} />
  </Box>
);

// Shared tile for the footer row (index, as-of readouts, model). Optional
// valueColor for tiles where sign matters (e.g. funding rate direction).
const Tile: FC<{ label: string; value: string; valueColor?: string }> = ({
  label,
  value,
  valueColor,
}) => (
  <Box sx={{ flex: 1, backgroundColor: TILE_BG, px: 1.125, py: 0.75 }}>
    <Typography
      sx={{ fontSize: 8, color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '.06em' }}
    >
      {label}
    </Typography>
    <Typography
      sx={{ fontFamily: 'monospace', fontSize: 11.5, color: valueColor ?? TEXT_PRIMARY, mt: 0.25 }}
    >
      {value}
    </Typography>
  </Box>
);

// Funding rate can legitimately print as a large annualized number during
// crowded/panicky positioning (see PerpPoint's header comment) — shown
// as-is, no clamping. Sign-coloured like RateCell (positive = longs paying
// shorts, matches the platform-wide contango/backwardation convention).
const fmtFundingRate = (v: number | null): string =>
  v === null ? '—' : `${v >= 0 ? '+' : ''}${(v * 100).toFixed(2)}%`;

const Banner: FC<{ token: string; ccy: string }> = ({ token, ccy }) => (
  <Stack
    direction="row"
    alignItems="center"
    sx={{ backgroundColor: DILETTA_BANNER_BG, px: 1.625, py: 1.125 }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.75, lineHeight: 0 }}>
      <CryptoIcon symbol={token} size={16} />
    </Box>
    <Typography
      sx={{ color: DILETTA_TITLE, fontWeight: 700, fontSize: 13, letterSpacing: '.04em' }}
    >
      {token}{' '}
      <Box component="span" sx={{ color: ACCENT_SOFT, fontWeight: 400, fontSize: 10 }}>
        {'·'} {ccy}
      </Box>
    </Typography>
  </Stack>
);

const fmtSpot = (v: number | null, decimals: number): string =>
  v === null
    ? '—'
    : v.toLocaleString('en-GB', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      });

// "As Of" elapsed readout — "12s ago" / "3m 4s ago", or "—" before the
// first successful poll lands.
const fmtElapsed = (lastUpdated: number | null, now: Date): string => {
  if (lastUpdated === null) return '—';
  const ms = now.getTime() - lastUpdated;
  if (!globalThis.Number.isFinite(ms) || ms < 0) return '0s ago';
  const totalSecs = Math.floor(ms / 1000);
  const mins = Math.floor(totalSecs / 60);
  const secs = totalSecs % 60;
  return `${mins > 0 ? `${mins}m ${secs}s` : `${secs}s`} ago`;
};

const CarryCurveCard: FC<{
  market: CarryCurveMarket;
  isLoading?: boolean;
  error?: string | null;
  lastUpdated?: number | null;
  futuresLastUpdated?: number | null;
  now?: Date;
}> = ({
  market,
  isLoading,
  error,
  lastUpdated = null,
  futuresLastUpdated = null,
  now = new Date(),
}) => {
  const { token, ccy, spot, decimals, curve, perp } = market;

  if (curve.length === 0) {
    const message = error
      ? `Vol surface error: ${error}`
      : isLoading
        ? 'Loading vol surface…'
        : spot === null
          ? 'Waiting on live spot…'
          : 'No listed expiries returned';
    return (
      <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
        <Banner token={token} ccy={ccy} />
        <Typography
          sx={{
            color: error ? NEGATIVE : TEXT_MUTED,
            fontSize: 11,
            px: 1.75,
            py: 2,
            textAlign: 'center',
          }}
        >
          {message}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ ...dilettaPanelSx, overflow: 'hidden', borderRadius: '9px' }}>
      <Banner token={token} ccy={ccy} />

      <Box sx={{ ...ROW_GRID_SX, pt: 0.875, pb: 0.5 }}>
        <Box />
        <DividerCell />
        <Typography sx={{ gridColumn: 'span 4', ...GROUP_HEAD_SX }}>Futures Market</Typography>
        <DividerCell />
        <Typography sx={{ gridColumn: 'span 7', ...GROUP_HEAD_SX }}>Options Market</Typography>
      </Box>

      <Box
        sx={{
          ...ROW_GRID_SX,
          alignItems: 'flex-end',
          pt: 0.375,
          pb: 0.5,
          borderTop: `1px solid ${BORDER_SUBTLE}`,
        }}
      >
        <Typography sx={{ ...HEAD_SX }}>Tenor</Typography>
        <DividerCell />
        <StackedHeadCell label="Futures Mark" />
        <StackedHeadCell label="Fwd Points" />
        <StackedHeadCell label="Forward Factor" />
        <StackedHeadCell label="Futures Carry" />
        <DividerCell />
        <StackedHeadCell label="Future" />
        <StackedHeadCell label="Fwd Points" />
        <StackedHeadCell label="Forward Factor" />
        <StackedHeadCell label="USD Discount" />
        <StackedHeadCell label={`${token} Discount`} />
        <StackedHeadCell label="Forward Carry" />
        <StackedHeadCell label="Basis" />
      </Box>

      <Box>
        {perp && <PerpRow perp={perp} decimals={decimals} />}
        {curve.map((point) => (
          <CurveRow key={point.tenor} {...point} decimals={decimals} now={now} />
        ))}
      </Box>

      <Stack
        direction="row"
        spacing="1px"
        sx={{ backgroundColor: DILETTA_DIVIDER, borderTop: `1px solid ${BORDER_SUBTLE}` }}
      >
        <Tile label="Index" value={fmtSpot(perp?.futuresIndex ?? null, decimals)} />
        <Tile
          label="Funding (Ann.)"
          value={fmtFundingRate(perp?.fundingRateAnnualized ?? null)}
          valueColor={
            perp?.fundingRateAnnualized == null
              ? undefined
              : perp.fundingRateAnnualized >= 0
                ? POSITIVE
                : NEGATIVE
          }
        />
        <Tile label="Options As Of" value={fmtElapsed(lastUpdated, now)} />
        <Tile label="Futures As Of" value={fmtElapsed(futuresLastUpdated, now)} />
        <Tile label="Model" value="PCP (options-implied)" />
      </Stack>
    </Box>
  );
};

export default CarryCurveCard;
